import { useEffect, useState } from "react";
import { api, API, formatApiErrorDetail } from "../lib/api";
import { Layout } from "../components/Layout";
import { toast } from "sonner";
import {
  Cube, Lightning, MagnifyingGlass, Trash, Terminal, ShieldCheck,
  FloppyDisk, Archive, ClipboardText, DownloadSimple, CheckCircle,
  CircleNotch, ArrowsClockwise, Package, HardDrives,
} from "@phosphor-icons/react";

const labelCls = "block font-mono text-[10px] uppercase tracking-wider text-[#71717A] mb-2";
const inputCls = "w-full bg-[#050505] border border-[#27272A] px-3 py-2 text-sm font-mono focus:border-[#00F0FF] focus:outline-none transition-colors duration-200";

const KIND_COLOR = { pkg: "#00F0FF", dmg: "#B37FEB", zip: "#00FF41" };

export default function ForgePkg() {
  const [providers, setProviders] = useState([]);
  const [provider, setProvider] = useState("addigy");
  const [catalog, setCatalog] = useState([]);
  const [search, setSearch] = useState("");
  const [selected, setSelected] = useState([]); // [{id,name,kind,team_id,version,url,resolving,error}]

  const [enforce, setEnforce] = useState(true);
  const [schedule, setSchedule] = useState(false);
  const [hour, setHour] = useState(5);

  const [building, setBuilding] = useState(false);
  const [result, setResult] = useState(null);
  const [tab, setTab] = useState(0);
  const [projects, setProjects] = useState([]);
  const [saveTarget, setSaveTarget] = useState("new");
  const [saving, setSaving] = useState(false);
  const [bundling, setBundling] = useState(false);
  const [fullPkg, setFullPkg] = useState(false);

  useEffect(() => {
    api.get("/providers").then((r) => setProviders(r.data)).catch(() => {});
    api.get("/forgepkg/catalog").then((r) => setCatalog(r.data)).catch(() => {});
    api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
  }, []);

  const isSelected = (id) => selected.some((s) => s.id === id);

  const resolveOne = async (id) => {
    setSelected((prev) => prev.map((s) => (s.id === id ? { ...s, resolving: true, error: "" } : s)));
    try {
      const { data } = await api.post("/forgepkg/resolve", { app_id: id });
      setSelected((prev) => prev.map((s) => (s.id === id
        ? { ...s, resolving: false, version: data.version, url: data.url, team_id: data.team_id, kind: data.kind, hosted: data.hosted, error: "" }
        : s)));
    } catch (err) {
      const msg = formatApiErrorDetail(err.response?.data?.detail) || "Could not resolve latest";
      setSelected((prev) => prev.map((s) => (s.id === id ? { ...s, resolving: false, error: msg } : s)));
      toast.error(msg);
    }
  };

  const toggle = (app) => {
    if (isSelected(app.id)) {
      setSelected((prev) => prev.filter((s) => s.id !== app.id));
      return;
    }
    setSelected((prev) => [...prev, {
      id: app.id, name: app.name, kind: app.kind, team_id: app.team_id, version: "", url: "", resolving: true, error: "",
    }]);
    resolveOne(app.id);
  };
  const removeSel = (id) => setSelected((prev) => prev.filter((s) => s.id !== id));

  const readyApps = selected.filter((s) => s.url && !s.resolving);

  const build = async () => {
    if (!readyApps.length) { toast.error("Add and resolve at least one app first"); return; }
    setBuilding(true);
    try {
      const params = {
        apps: readyApps.map((s) => ({ id: s.id, name: s.name, kind: s.kind, team_id: s.team_id, version: s.version, url: s.url })),
        enforce_team_id: enforce, schedule, schedule_hour: Number(hour),
      };
      const { data } = await api.post("/forgepkg/build", { provider, params });
      setResult(data); setTab(0);
      toast.success("ForgePkg deploy script generated");
    } catch (err) {
      toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Build failed");
    } finally { setBuilding(false); }
  };

  const artifacts = result?.artifacts || [];
  const active = artifacts[tab];

  const copyOut = async () => {
    try { await navigator.clipboard.writeText(active.content); toast.success("Copied"); }
    catch { toast.error("Copy failed — select manually"); }
  };
  const downloadOut = () => {
    const blob = new Blob([active.content], { type: "text/plain" });
    const a = document.createElement("a");
    a.href = URL.createObjectURL(blob); a.download = active.filename; a.click();
  };
  const save = async () => {
    const script = artifacts.find((a) => a.type === "script");
    if (!script) return;
    setSaving(true);
    try {
      const body = { title: `ForgePkg (${readyApps.length} apps)`, provider, output_type: "script",
        content: script.content, language: "bash", filename: script.filename, deploy_notes: result.deploy_notes, prompt: "" };
      if (saveTarget === "new") await api.post("/projects", body);
      else await api.post(`/projects/${saveTarget}/versions`, { ...body, note: "ForgePkg" });
      toast.success("Saved to projects");
      api.get("/projects").then((r) => setProjects(r.data)).catch(() => {});
    } catch (err) { toast.error(formatApiErrorDetail(err.response?.data?.detail) || "Save failed"); }
    finally { setSaving(false); }
  };
  const downloadBundle = async () => {
    if (!artifacts.length) return;
    setBundling(true);
    try {
      const resp = await fetch(`${API}/bundle-multi`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ title: "ForgePkg", provider, deploy_notes: result.deploy_notes,
          files: artifacts.map((a) => ({ filename: a.filename, content: a.content })) }),
      });
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `${provider}-forgepkg.zip`; a.click();
      toast.success("Bundle downloaded");
    } catch { toast.error("Bundle failed"); }
    finally { setBundling(false); }
  };

  const downloadFullPackage = async () => {
    if (!readyApps.length) { toast.error("Resolve at least one app first"); return; }
    setFullPkg(true);
    try {
      const resp = await fetch(`${API}/forgepkg/full-package`, {
        method: "POST", headers: { "Content-Type": "application/json" }, credentials: "include",
        body: JSON.stringify({ provider, params: {
          apps: readyApps.map((s) => ({ id: s.id, name: s.name, kind: s.kind, team_id: s.team_id, version: s.version, url: s.url })),
          enforce_team_id: enforce, schedule, schedule_hour: Number(hour),
        } }),
      });
      if (!resp.ok) {
        let msg = "Full package download failed";
        try { const j = await resp.json(); msg = j.detail || msg; } catch { /* non-json */ }
        toast.error(msg); return;
      }
      const blob = await resp.blob();
      const a = document.createElement("a");
      a.href = URL.createObjectURL(blob); a.download = `${provider}-forgepkg-full.zip`; a.click();
      toast.success("Full package downloaded (installers + script)");
    } catch { toast.error("Full package download failed"); }
    finally { setFullPkg(false); }
  };

  const filtered = catalog.filter((c) => c.name.toLowerCase().includes(search.toLowerCase()));

  return (
    <Layout>
      <div className="border-b border-[#27272A] px-8 py-6">
        <h1 className="text-2xl tracking-tight flex items-center gap-2">
          <Cube size={22} weight="fill" className="text-[#00F0FF]" /> ForgePkg
          <span className="ml-2 font-mono text-[9px] uppercase tracking-wider border border-[#B37FEB] text-[#C79BF0] px-2 py-0.5">AutoPkg, modernized</span>
        </h1>
        <p className="text-sm text-[#A1A1AA] font-mono mt-1">
          No recipes. No build host. No git. Pick apps → we resolve the latest from each vendor, verify the Apple Team&nbsp;ID, and hand you one deploy script.
        </p>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-[1fr_440px] gap-px bg-[#27272A]">
        <div className="bg-[#0A0A0B] p-8 space-y-6">
          <div>
            <label className={labelCls}>MDM Provider</label>
            <div className="flex flex-wrap gap-2">
              {providers.map((p) => (
                <button key={p.id} data-testid={`fp-provider-${p.id}`} onClick={() => setProvider(p.id)}
                  className={`flex items-center gap-2 px-3 py-2 border text-xs transition-colors duration-200 ${provider === p.id ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"}`}>
                  <span className="w-2 h-2 rounded-full" style={{ background: p.color }} />{p.name}
                </button>
              ))}
            </div>
          </div>

          {/* App picker */}
          <div>
            <label className={labelCls}>Apps to keep current</label>
            <div className="relative mb-3">
              <MagnifyingGlass size={14} className="absolute left-3 top-1/2 -translate-y-1/2 text-[#71717A]" />
              <input data-testid="fp-search" value={search} onChange={(e) => setSearch(e.target.value)} placeholder="Search apps…" className={inputCls + " pl-8"} />
            </div>
            <div className="grid grid-cols-2 gap-2 max-h-56 overflow-y-auto pr-1" data-testid="fp-catalog">
              {filtered.map((app) => (
                <button key={app.id} data-testid={`fp-app-${app.id}`} onClick={() => toggle(app)}
                  className={`flex items-center gap-2 px-3 py-2 border text-left text-xs transition-colors ${isSelected(app.id) ? "border-[#00F0FF] bg-[#00F0FF]/5 text-[#FAFAFA]" : "border-[#27272A] text-[#A1A1AA] hover:border-[#3f3f46]"}`}>
                  {isSelected(app.id) ? <CheckCircle size={16} weight="fill" className="text-[#00F0FF] shrink-0" /> : <Package size={16} className="text-[#52525B] shrink-0" />}
                  <span className="truncate flex-1">{app.name}</span>
                  <span className="font-mono text-[9px] uppercase" style={{ color: KIND_COLOR[app.kind] || "#71717A" }}>{app.kind}</span>
                </button>
              ))}
            </div>
          </div>

          {/* Resolved selection */}
          {selected.length > 0 && (
            <div data-testid="fp-selected">
              <label className={labelCls}>Selected &amp; resolved</label>
              <div className="space-y-2">
                {selected.map((s) => (
                  <div key={s.id} data-testid={`fp-sel-${s.id}`} className="flex items-center gap-3 border border-[#27272A] bg-[#0f0f11] px-3 py-2">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ background: KIND_COLOR[s.kind] || "#71717A" }} />
                    <div className="min-w-0 flex-1">
                      <div className="text-sm text-[#FAFAFA] truncate">{s.name}</div>
                      {s.resolving ? (
                        <div className="font-mono text-[10px] text-[#71717A] flex items-center gap-1.5"><CircleNotch size={11} className="animate-spin" /> resolving latest…</div>
                      ) : s.error ? (
                        <div className="font-mono text-[10px] text-[#FF5F56] truncate">{s.error}</div>
                      ) : (
                        <div className="font-mono text-[10px] text-[#A1A1AA] flex items-center gap-2 flex-wrap">
                          <span className="text-[#00FF41]" data-testid={`fp-ver-${s.id}`}>{!s.version || s.version === "latest" ? "latest" : (s.version[0] === "v" ? s.version : "v" + s.version)}</span>
                          {s.hosted && (
                            <span className="flex items-center gap-1 text-[#B37FEB]" data-testid={`fp-hosted-${s.id}`} title="Served from MDM Forge hosted storage"><HardDrives size={11} weight="fill" /> hosted</span>
                          )}
                          {s.team_id ? (
                            <span className="flex items-center gap-1 text-[#00F0FF]"><ShieldCheck size={11} weight="fill" /> {s.team_id}</span>
                          ) : (
                            <span className="text-[#71717A]">unverified</span>
                          )}
                        </div>
                      )}
                    </div>
                    <button data-testid={`fp-refresh-${s.id}`} onClick={() => resolveOne(s.id)} disabled={s.resolving}
                      className="text-[#71717A] hover:text-[#00F0FF] disabled:opacity-40" title="Re-resolve latest">
                      <ArrowsClockwise size={14} />
                    </button>
                    <button data-testid={`fp-remove-${s.id}`} onClick={() => removeSel(s.id)} className="text-[#71717A] hover:text-[#FF5F56]"><Trash size={14} /></button>
                  </div>
                ))}
              </div>
            </div>
          )}

          {/* Options */}
          <div>
            <label className={labelCls}>Integrity &amp; scheduling</label>
            <div className="flex flex-wrap items-center gap-3">
              <button data-testid="fp-enforce" onClick={() => setEnforce(!enforce)}
                className={`flex items-center gap-1.5 px-3 py-2 border text-xs ${enforce ? "border-[#00F0FF] text-[#00F0FF]" : "border-[#27272A] text-[#71717A]"}`}>
                <ShieldCheck size={14} weight={enforce ? "fill" : "regular"} /> Enforce Team ID
              </button>
              <button data-testid="fp-schedule" onClick={() => setSchedule(!schedule)}
                className={`px-3 py-2 border text-xs ${schedule ? "border-[#00FF41] text-[#00FF41]" : "border-[#27272A] text-[#71717A]"}`}>Daily scheduled run</button>
              {schedule && (
                <div className="flex items-center gap-2">
                  <span className="text-[10px] font-mono text-[#71717A]">at</span>
                  <input data-testid="fp-hour" type="number" min="0" max="23" value={hour} onChange={(e) => setHour(e.target.value)} className={inputCls + " w-20"} />
                  <span className="text-[10px] font-mono text-[#71717A]">:00</span>
                </div>
              )}
            </div>
            <p className="font-mono text-[10px] text-[#52525B] mt-2">
              {enforce ? "Installs are blocked if the downloaded binary's Team ID doesn't match." : "Team ID mismatches are logged but installs continue."}
            </p>
          </div>

          <button data-testid="fp-build-btn" onClick={build} disabled={building || !readyApps.length}
            className="flex items-center gap-2 bg-[#00F0FF] text-[#0A0A0B] font-medium px-5 py-2.5 text-sm hover:bg-[#00d4e0] transition-colors duration-200 disabled:opacity-50">
            <Lightning size={16} weight="fill" /> {building ? "Building…" : `Forge deploy script${readyApps.length ? ` (${readyApps.length})` : ""}`}
          </button>
        </div>

        {/* Output */}
        <div className="bg-[#0A0A0B] p-8">
          <div className="flex items-center gap-2 mb-4 font-mono text-xs text-[#71717A] uppercase tracking-wider">
            <Terminal size={14} /> Output
          </div>
          {!result && <p className="text-sm text-[#71717A] font-mono">Pick apps and forge your script. Each app is resolved live from its official vendor source — always the latest version, no recipe maintenance.</p>}
          {result && active && (
            <div data-testid="fp-output">
              <div className="flex gap-1 mb-3 border-b border-[#27272A]">
                {artifacts.map((a, i) => (
                  <button key={i} data-testid={`fp-tab-${i}`} onClick={() => setTab(i)}
                    className={`px-3 py-2 text-xs font-mono border-b-2 -mb-px ${tab === i ? "border-[#00F0FF] text-[#00F0FF]" : "border-transparent text-[#71717A]"}`}>{a.label}</button>
                ))}
              </div>
              <div className="flex items-center gap-2 mb-3">
                <button data-testid="fp-copy-btn" onClick={copyOut} className="flex items-center gap-1.5 border border-[#27272A] px-3 py-1.5 text-xs hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"><ClipboardText size={13} /> Copy</button>
                <button data-testid="fp-download-btn" onClick={downloadOut} className="flex items-center gap-1.5 border border-[#27272A] px-3 py-1.5 text-xs hover:border-[#00F0FF] hover:text-[#00F0FF] transition-colors"><DownloadSimple size={13} /> Download</button>
              </div>
              <pre className="bg-[#050505] border border-[#27272A] p-4 overflow-auto max-h-[360px] font-mono text-[11px] text-[#C7C7CC] leading-relaxed whitespace-pre-wrap">{active.content}</pre>
              <p className="text-[10px] font-mono text-[#71717A] mt-3">{result.deploy_notes}</p>
              <select data-testid="fp-save-target" value={saveTarget} onChange={(e) => setSaveTarget(e.target.value)} className={inputCls + " mt-4"}>
                <option value="new">+ New project</option>
                {projects.map((p) => (<option key={p.id} value={p.id}>Add version to: {p.title}</option>))}
              </select>
              <div className="flex items-center gap-2 mt-2">
                <button data-testid="fp-save-btn" onClick={save} disabled={saving} className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors disabled:opacity-50"><FloppyDisk size={16} /> {saving ? "…" : "Save"}</button>
                <button data-testid="fp-bundle-btn" onClick={downloadBundle} disabled={bundling} className="flex items-center gap-2 border border-[#0A84FF] text-[#0A84FF] px-4 py-2 text-sm hover:bg-[#0A84FF]/10 transition-colors disabled:opacity-50"><Archive size={16} /> {bundling ? "…" : "Bundle all"}</button>
              </div>
              <button data-testid="fp-fullpkg-btn" onClick={downloadFullPackage} disabled={fullPkg} title="Download the real installers bundled with the deploy script" className="flex items-center gap-2 border border-[#00FF41] text-[#00FF41] px-4 py-2 text-sm hover:bg-[#00FF41]/10 transition-colors disabled:opacity-50 mt-2 w-full justify-center"><Package size={16} weight="fill" /> {fullPkg ? "Fetching installers…" : "Download full package (with installers)"}</button>
            </div>
          )}
        </div>
      </div>
    </Layout>
  );
}
